import type { AppDispatch, RootState } from '@/store/store'
import { getFolderChildren } from '@/user_files/folders/fetch/get-folder-children'
import { createFolder } from '@/user_files/folders/fetch/post-create-folder'
import { UserFoldersActions } from '@/user_files/folders/store/slice'
import { selectChildFolders, selectCurrentFolderId, selectFolderChildrenFetchPending } from '@/user_files/folders/store/selectors'
import { cacheResolvedFolders } from '@/user_files/folders/utils/cache-resolved-folders'

type GetOrCreateFolderIdParams = {
  dispatch: AppDispatch
  getState: () => RootState
  parentId: string
  name: string
}

const inFlightFolderRequests = new Map<string, Promise<string>>()

/**
 * Matches on name, but only among folders Matter Files can actually write into.
 *
 * A provider-synced folder can sit beside a Paxton-owned one with the same name — `selectChildFolders`
 * deliberately keeps synced folders for pickers, and the drive listing hides the active ones, so the
 * synced sibling is invisible to the user while still being a match candidate. Resolving to it sends
 * the upload at a folder the backend external-storage guard rejects. `external_storage` is checked
 * rather than "actively synced" because the guard blocks writes for as long as the link exists,
 * tombstoned included.
 */
function getExistingChildFolderId(state: RootState, parentId: string, name: string): string | null {
  const child = selectChildFolders(state, { parentId }).find((folder) => folder.name === name && !folder.external_storage)
  return child?.id ?? null
}

/**
 * Answers "does this name already exist here?".
 *
 * The whole response is cached rather than just the match, so the remaining names in a dropped tree
 * resolve from the cached check above instead of draining this listing again for each one. Row order
 * does not matter to a name lookup, so this takes no sort.
 */
async function findChildFolderIdOnServer(params: GetOrCreateFolderIdParams): Promise<string | null> {
  const response = await getFolderChildren(params.parentId)
  cacheResolvedFolders(params, response.folders)

  // Same rule as the cached lookup above: a provider-synced sibling can share the name and is not
  // a folder Matter Files can write into.
  const match = response.folders.find((folder) => folder.name === params.name && !folder.external_storage)
  return match?.id ?? null
}

/**
 * Fetches the parent's listing so a folder this resolve created or found is shown in it.
 *
 * The backend owns row order, so the listing is the only thing that knows where the folder belongs.
 * Nothing puts the folder into the cache ahead of it: a locally added row could only go on the end,
 * which would show it in the wrong place and then move it once the listing arrived.
 *
 * Two parents need that listing now. Every other one is left alone: folder-child requests always
 * refresh, so a nested parent picks the folder up the next time a browse page or picker enters it,
 * and draining a paginated listing here would spend a request per created folder on rows nobody is
 * displaying.
 */
function reconcileParentListing(params: GetOrCreateFolderIdParams): void {
  const state = params.getState()
  const isDriveCurrentFolder = selectCurrentFolderId(state) === params.parentId
  // A load already running for this folder was issued before the folder existed, so it lands as a
  // full replacement without it. `currentFolderId` is only the Matter Files route; a picker or the
  // move dialog renders the same cache under its own browse folder and refreshes only when that
  // folder changes, so it would sit on that listing indefinitely. Dispatching supersedes it without
  // a second concurrent request: the listener queues one follow-up refresh behind an in-flight load.
  const hasListingInFlight = selectFolderChildrenFetchPending(state, { folderId: params.parentId })

  if (!isDriveCurrentFolder && !hasListingInFlight) return

  params.dispatch(UserFoldersActions.fetchFolderChildren({ folderId: params.parentId }))
}

/**
 * Resolves the id without putting a created folder into the parent's listing, so the caller owns
 * introducing it once, in the server's order.
 */
async function resolveOrCreateChildFolderId(params: GetOrCreateFolderIdParams): Promise<string> {
  const serverFolderId = await findChildFolderIdOnServer(params)
  if (serverFolderId) return serverFolderId

  try {
    const folder = await createFolder({ parent_id: params.parentId, name: params.name })
    cacheResolvedFolders(params, [folder])
    return folder.id
  } catch (error) {
    // A concurrent caller may have won the race for this name; the create then fails with a
    // conflict and the folder it lost to is the one to use.
    const refetchedId = await findChildFolderIdOnServer(params)
    if (refetchedId) return refetchedId
    throw error
  }
}

async function createChildFolderId(params: GetOrCreateFolderIdParams): Promise<string> {
  const existingId = getExistingChildFolderId(params.getState(), params.parentId, params.name)
  if (existingId) return existingId

  const folderId = await resolveOrCreateChildFolderId(params)
  // Every path that gets here found or created a folder the cached listing does not hold in the
  // server's order. A throw skips this, because nothing changed.
  reconcileParentListing(params)
  return folderId
}

/**
 * Finds or creates a single named child folder under `parentId`.
 * Dedupes concurrent requests for the same parent+name so parallel callers
 * (e.g. many files sharing a dropped subfolder) don't race-create duplicates.
 */
export function getOrCreateFolderId(params: GetOrCreateFolderIdParams): Promise<string> {
  const cacheKey = `${params.parentId}:${params.name}`
  const existingInFlightRequest = inFlightFolderRequests.get(cacheKey)
  if (existingInFlightRequest) return existingInFlightRequest

  const promise = createChildFolderId(params).finally(() => {
    inFlightFolderRequests.delete(cacheKey)
  })
  inFlightFolderRequests.set(cacheKey, promise)
  return promise
}

/**
 * Walks a list of folder-name segments starting at `rootId`, resolving or creating
 * each level in turn. Used to recreate a dropped directory's nested structure.
 */
export async function getOrCreateFolderPathId(params: {
  dispatch: AppDispatch
  getState: () => RootState
  rootId: string
  segments: string[]
}): Promise<string> {
  let parentId = params.rootId
  for (const name of params.segments) {
    parentId = await getOrCreateFolderId({ dispatch: params.dispatch, getState: params.getState, parentId, name })
  }
  return parentId
}
